import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Global keyboard shortcuts. Keys are lowercase combos ('mod+k', 'c', '?', '[') or two-key
 * sequences ('g p'). Ignored while typing in a field and while a modal overlay is open, except
 * combos with `mod`.
 */
export type HotkeyMap = Record<string, (event: KeyboardEvent) => void>;

const SEQUENCE_TIMEOUT = 900;

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.getAttribute('role') === 'combobox'
  );
}

function modalOpen(): boolean {
  return document.querySelector('[role="dialog"][data-state="open"]:not([data-nonmodal])') !== null;
}

export function comboOf(event: KeyboardEvent): string {
  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key.toLowerCase();
  const mod = event.metaKey || event.ctrlKey;
  return mod ? `mod+${key}` : key;
}

export function useHotkeys(map: HotkeyMap, enabled = true): void {
  const latest = useRef(map);
  useLayoutEffect(() => {
    latest.current = map;
  });

  useEffect(() => {
    if (!enabled) return undefined;
    let pending: string | null = null;
    let timer: number | undefined;

    function onKeyDown(event: KeyboardEvent): void {
      if (event.defaultPrevented || event.isComposing || event.altKey) return;
      const combo = comboOf(event);
      const withMod = combo.startsWith('mod+');
      if (!withMod && (isTyping(event.target) || modalOpen())) return;

      if (pending !== null) {
        const sequence = `${pending} ${combo}`;
        pending = null;
        window.clearTimeout(timer);
        const handler = latest.current[sequence];
        if (handler) {
          event.preventDefault();
          handler(event);
          return;
        }
      }
      if (Object.keys(latest.current).some((k) => k.startsWith(`${combo} `))) {
        pending = combo;
        timer = window.setTimeout(() => {
          pending = null;
        }, SEQUENCE_TIMEOUT);
        return;
      }
      const handler = latest.current[combo];
      if (handler) {
        event.preventDefault();
        handler(event);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(timer);
    };
  }, [enabled]);
}
